import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REFRESH_EVENT } from '../lib/notificationActions';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
    User, FileText, Video as VideoIcon, Shield, Upload, Check, X, Clock,
    Briefcase, MapPin, DollarSign, ChevronRight, ChevronDown, AlertCircle, Download, CreditCard,
    Phone, Printer, Smartphone, Camera, Calendar, ExternalLink, Eye, Plus, Trash2, Lock
} from 'lucide-react';
import DocumentPreviewModal from '../components/DocumentPreviewModal';
import StripePaymentModal from '../components/StripePaymentModal';
import EmailPreferencesPanel from '../components/EmailPreferencesPanel';
import WorkerJobBoard from '../components/WorkerJobBoard';
import WorkerMyApplications from '../components/WorkerMyApplications';
import { isValidUrl, truncate as truncateUrl } from '../lib/videoEmbed';

const LANGUAGES = ['English', 'Arabic', 'French', 'Swahili', 'Luganda'];

export default function WorkerDashboard() {
    const { user, refreshUser } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = (() => {
        const t = searchParams.get('tab');
        if (t === 'my-applications') return 'my-applications';
        if (t === 'job-openings') return 'job-openings';
        return 'dashboard';
    })();
    const [activeWorkerTab, setActiveWorkerTab] = useState(initialTab);
    const [profile, setProfile] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [offers, setOffers] = useState([]);
    const [activeOffer, setActiveOffer] = useState(null);
    const [visaDoc, setVisaDoc] = useState(null);
    const [ticketDoc, setTicketDoc] = useState(null);
    const [offerLetterDoc, setOfferLetterDoc] = useState(null);
    const [interviews, setInterviews] = useState([]);
    const [showPastInterviews, setShowPastInterviews] = useState(false);
    // _nowTick is intentionally only used as a render trigger from the 60s interval below
    const [, setNowTick] = useState(0);
    const [loading, setLoading] = useState(true);
    const [step, setStep] = useState(1);
    const [saving, setSaving] = useState(false);
    const [view, setView] = useState('onboarding');

    // Document preview modal (Fix 5)
    const [previewDoc, setPreviewDoc] = useState(null);
    const [previewMeta, setPreviewMeta] = useState(null);

    // Additional documents state (Fix 3)
    const [additionalLabel, setAdditionalLabel] = useState('');
    const [uploadingAdditional, setUploadingAdditional] = useState(false);
    const [additionalError, setAdditionalError] = useState('');

    // OTP state
    const [otpPhone, setOtpPhone] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpError, setOtpError] = useState('');

    // Appointment state
    const [medPartners, setMedPartners] = useState([]);
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [partnerSlots, setPartnerSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [appointment, setAppointment] = useState(null);
    const [bookingStep, setBookingStep] = useState('partners');
    const [bookingLoading, setBookingLoading] = useState(false);
    const [showMedicalBooking, setShowMedicalBooking] = useState(false);

    // Profile form
    const [formName, setFormName] = useState('');
    const [formDob, setFormDob] = useState('');
    const [formCountry, setFormCountry] = useState('');
    // Country dropdown supports an "Other" option; when selected the worker
    // types their country into `formCountryOther`. On save we send whichever
    // value is applicable so the backend always sees a plain country string.
    const [formCountryOther, setFormCountryOther] = useState('');
    const COUNTRY_LIST = ['Uganda', 'Kenya', 'Tanzania', 'Ethiopia', 'Ghana', 'Nigeria', 'Rwanda', 'Egypt', 'Morocco', 'Sudan'];
    const [formInterests, setFormInterests] = useState([]);
    const [formLanguages, setFormLanguages] = useState([]);
    const [formExperience, setFormExperience] = useState('');
    const [formPhone, setFormPhone] = useState('');
    const [interestCategories, setInterestCategories] = useState([]);
    // Inline validation: { fieldKey: 'error message' } map. Empty = no errors.
    const [profileErrors, setProfileErrors] = useState({});

    // Video introduction link (replaces the old video file upload). Input state
    // lives separately from the saved profile.video_intro_url so the user can
    // edit + cancel without mutating the saved value.
    const [videoLinkInput, setVideoLinkInput] = useState('');
    const [videoLinkEditing, setVideoLinkEditing] = useState(false);
    const [videoLinkError, setVideoLinkError] = useState('');
    const [videoLinkSaving, setVideoLinkSaving] = useState(false);
    const [videoLinkSavedMsg, setVideoLinkSavedMsg] = useState('');

    const fetchData = useCallback(async () => {
        try {
            const [profileRes, offersRes, activeRes, apptRes, interviewsRes, interestsRes] = await Promise.all([
                api.get('/worker/profile'),
                api.get('/worker/offers'),
                api.get('/worker/active-offer'),
                api.get('/worker/appointment'),
                api.get('/worker/interviews'),
                api.get('/interests'),
            ]);
            const p = profileRes.data.profile;
            const d = profileRes.data.documents;
            setProfile(p);
            setDocuments(d);
            setOffers(offersRes.data.offers);
            setActiveOffer(activeRes.data.offer);
            setVisaDoc(activeRes.data.visa_document);
            setTicketDoc(activeRes.data.ticket_document);
            setOfferLetterDoc(activeRes.data.offer_letter_document);
            setAppointment(apptRes.data.appointment);
            setInterviews(interviewsRes.data.interviews || []);
            setInterestCategories(interestsRes.data.categories || []);

            setFormName(p.name || '');
            setFormDob(p.dob || '');
            // If the saved country is one of the 10 listed options use it as-is;
            // otherwise treat it as an "Other" free-text entry so the worker can
            // edit it. Empty (new profile) leaves the dropdown unselected.
            const savedCountry = p.country || '';
            if (savedCountry && !COUNTRY_LIST.includes(savedCountry)) {
                setFormCountry('Other');
                setFormCountryOther(savedCountry);
            } else {
                setFormCountry(savedCountry);
                setFormCountryOther('');
            }
            setFormInterests(p.interests || []);
            setFormLanguages(p.languages || []);
            setFormExperience(p.experience || '');
            setFormPhone(p.phone || '');

            // Determine step — worker goes to dashboard once profile + passport are in.
            // Interpol/Police Clearance is no longer required (it's optional, surfaced
            // in the "Complete Your Profile" checklist on the dashboard).
            const docTypes = new Set(d.map(doc => doc.doc_type));
            const hasPassport = docTypes.has('passport');

            if (!p.profile_complete) { setStep(1); setView('onboarding'); }
            else if (!hasPassport) { setStep(2); setView('onboarding'); }
            else { setStep(6); setView('dashboard'); }
        } catch (err) {
            console.error('Failed to fetch worker data:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Sync workflow tab with URL ?tab= for notification deep-links.
    useEffect(() => {
        const t = searchParams.get('tab');
        if (t === 'my-applications' || t === 'job-openings' || t === 'dashboard') {
            setActiveWorkerTab(t);
        }
    }, [searchParams]);

    const changeWorkerTab = (t) => {
        setActiveWorkerTab(t);
        const next = new URLSearchParams(searchParams);
        if (t === 'dashboard') next.delete('tab'); else next.set('tab', t);
        setSearchParams(next, { replace: true });
    };

    useEffect(() => { fetchData(); }, [fetchData]);

    // Lightweight 5s poll of just the active-offer data so visa-status changes
    // and newly-uploaded documents (visa / flight ticket / offer letter) reflect
    // on the worker dashboard in near-real-time without a full reload.
    useEffect(() => {
        // Only poll once user is past onboarding (i.e., has an active offer).
        if (view !== 'dashboard') return;
        const tick = async () => {
            try {
                const { data } = await api.get('/worker/active-offer');
                setActiveOffer(data.offer);
                setVisaDoc(data.visa_document);
                setTicketDoc(data.ticket_document);
                setOfferLetterDoc(data.offer_letter_document);
            } catch { /* silent — keep last good state */ }
        };
        const interval = setInterval(tick, 5000);
        return () => clearInterval(interval);
    }, [view]);

    // Silent refresh on notification click / arrival. Worker dashboard is
    // single-section so any refresh key just triggers fetchData.
    useEffect(() => {
        const handler = () => { fetchData(); };
        window.addEventListener(REFRESH_EVENT, handler);
        return () => window.removeEventListener(REFRESH_EVENT, handler);
    }, [fetchData]);

    // 60-second tick: re-evaluate active/past split as time advances so cards
    // auto-move once the 15-minute grace expires (no manual refresh required).
    useEffect(() => {
        const id = setInterval(() => setNowTick(t => t + 1), 60 * 1000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        if (step === 5 && !appointment) loadMedicalPartners();
    }, [step, appointment]);

    const validateProfile = () => {
        const errors = {};
        if (!formName.trim()) errors.name = 'Please enter your full name';
        if (!formDob) errors.dob = 'Please select your date of birth';
        if (!formCountry) errors.country = 'Please select your country';
        else if (formCountry === 'Other' && !formCountryOther.trim()) errors.country = 'Please type your country';
        if (!formInterests || formInterests.length === 0) errors.interests = 'Please select at least one interest';
        if (!formLanguages || formLanguages.length === 0) errors.languages = 'Please add at least one language';
        if (!formExperience.trim()) errors.experience = 'Please describe your work experience';
        return errors;
    };

    // Clear a single field's error as soon as the worker starts fixing it
    const clearError = (key) => {
        setProfileErrors(prev => {
            if (!prev[key]) return prev;
            const { [key]: _drop, ...rest } = prev;
            return rest;
        });
    };

    const saveProfile = async () => {
        // Validate every required field; show all errors at once
        const errors = validateProfile();
        setProfileErrors(errors);
        if (Object.keys(errors).length > 0) {
            // Scroll the first invalid field into view so the worker sees what's wrong
            const firstKey = Object.keys(errors)[0];
            const el = document.querySelector(`[data-field="${firstKey}"]`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        setSaving(true);
        try {
            const countryToSave = formCountry === 'Other' ? formCountryOther.trim() : formCountry;
            await api.put('/worker/profile', {
                name: formName, dob: formDob, country: countryToSave,
                interests: formInterests, languages: formLanguages,
                experience: formExperience, phone: formPhone,
            });
            await refreshUser();
            await fetchData();
        } catch (err) { console.error(err); }
        finally { setSaving(false); }
    };

    const uploadDocument = async (file, docType) => {
        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('doc_type', docType);
            await api.post('/worker/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            await fetchData();
        } catch (err) { console.error(err); }
        finally { setSaving(false); }
    };

    const respondToOffer = async (offerId, action) => {
        try {
            await api.post(`/worker/offers/${offerId}/respond`, { action });
            await fetchData();
        } catch (err) { console.error(err); }
    };

    // Dynamic document access fee (admin-configurable)
    const [feeUsd, setFeeUsd] = useState(500);
    const [feeError, setFeeError] = useState(false);

    // Fetch current platform fee on mount
    useEffect(() => {
        let mounted = true;
        api.get('/settings/document-access-fee')
            .then(r => { if (mounted) { setFeeUsd(Number(r.data.fee)); setFeeError(false); } })
            .catch(() => { if (mounted) setFeeError(true); });
        return () => { mounted = false; };
    }, []);

    const formatFee = (n) => `$${Number(n).toFixed(2)}`;

    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [paymentBanner, setPaymentBanner] = useState(null); // 'success' | 'error'
    const [paymentMessage, setPaymentMessage] = useState('');

    const onPaidSuccess = async () => {
        await refreshUser();
        await fetchData();
        setPaymentBanner('success');
        setPaymentMessage('Payment received. Your documents are now unlocked.');
    };

    const handleDownload = async (docType) => {
        try {
            const response = await api.get(`/worker/download/${docType}`, { responseType: 'blob' });
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${docType.replace('_', '-')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) { console.error(err); }
    };

    const handlePrint = async (docType) => {
        try {
            const response = await api.get(`/worker/download/${docType}`, { responseType: 'blob' });
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = url;
            document.body.appendChild(iframe);
            iframe.onload = () => {
                try { iframe.contentWindow.print(); }
                catch { window.open(url, '_blank'); }
            };
        } catch (err) { console.error(err); }
    };

    const sendOtp = async () => {
        setOtpLoading(true); setOtpError('');
        try { await api.post('/auth/send-otp', { phone: otpPhone }); setOtpSent(true); }
        catch (err) { setOtpError(err.response?.data?.detail || 'Failed'); }
        finally { setOtpLoading(false); }
    };

    const verifyOtp = async () => {
        setOtpLoading(true); setOtpError('');
        try { await api.post('/auth/verify-otp', { phone: otpPhone, code: otpCode }); setOtpVerified(true); await refreshUser(); await fetchData(); }
        catch (err) { setOtpError(err.response?.data?.detail || 'Invalid'); }
        finally { setOtpLoading(false); }
    };

    const loadMedicalPartners = async () => {
        try { const { data } = await api.get('/worker/medical-partners'); setMedPartners(data.partners); }
        catch (err) { console.error(err); }
    };

    const loadPartnerSlots = async (partnerId) => {
        try {
            const { data } = await api.get(`/worker/medical-partners/${partnerId}/slots`);
            setSelectedPartner(data.partner); setPartnerSlots(data.slots); setBookingStep('slots');
        } catch (err) { console.error(err); }
    };

    const bookAppointment = async () => {
        if (!selectedPartner || !selectedSlot) return;
        setBookingLoading(true);
        try { await api.post('/worker/book-appointment', { partner_id: selectedPartner.id, slot_id: selectedSlot.id }); await fetchData(); }
        catch (err) { console.error(err); }
        finally { setBookingLoading(false); }
    };

    const uploadOptionalVideo = async (file) => {
        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('doc_type', 'video_intro');
            await api.post('/worker/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            await fetchData();
        } catch (err) { console.error(err); }
        finally { setSaving(false); }
    };

    // Save / clear the video introduction LINK. This is the new primary path —
    // file upload above is retained for any legacy worker still on the old flow
    // until they replace their file with a link (we never delete their file).
    const saveVideoLink = async () => {
        const url = (videoLinkInput || '').trim();
        if (url && !isValidUrl(url)) {
            setVideoLinkError('Please enter a valid link starting with http:// or https://');
            return;
        }
        if (url.length > 500) {
            setVideoLinkError('Link must be 500 characters or less');
            return;
        }
        setVideoLinkError('');
        setVideoLinkSaving(true);
        try {
            await api.put('/worker/video-intro', { url });
            await fetchData();
            setVideoLinkSavedMsg(url ? 'Video link saved successfully.' : 'Video link removed.');
            setVideoLinkEditing(false);
            setTimeout(() => setVideoLinkSavedMsg(''), 3500);
        } catch (err) {
            const detail = err?.response?.data?.detail || 'Could not save the video link — please try again.';
            setVideoLinkError(detail);
        } finally {
            setVideoLinkSaving(false);
        }
    };

    const removeVideoLink = async () => {
        setVideoLinkInput('');
        setVideoLinkSaving(true);
        try {
            await api.put('/worker/video-intro', { url: '' });
            await fetchData();
            setVideoLinkSavedMsg('Video link removed.');
            setVideoLinkEditing(false);
            setTimeout(() => setVideoLinkSavedMsg(''), 3500);
        } catch (err) { console.error(err); }
        finally { setVideoLinkSaving(false); }
    };

    // Additional documents (Fix 3)
    const uploadAdditional = async (file) => {
        setAdditionalError('');
        if (!additionalLabel.trim()) {
            setAdditionalError('Please provide a label for this document');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setAdditionalError('File exceeds 10 MB limit');
            return;
        }
        setUploadingAdditional(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('doc_type', 'additional');
            formData.append('doc_label', additionalLabel.trim());
            await api.post('/worker/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setAdditionalLabel('');
            await fetchData();
        } catch (err) {
            setAdditionalError(err.response?.data?.detail || 'Upload failed');
        } finally {
            setUploadingAdditional(false);
        }
    };

    const deleteAdditional = async (docId) => {
        try {
            await api.delete(`/worker/documents/${docId}`);
            await fetchData();
        } catch (err) { console.error(err); }
    };

    // Open preview for a protected worker document (visa / ticket / offer_letter — behind payment)
    const openProtectedPreview = (docType, doc) => {
        setPreviewMeta({
            storage_path: null,
            original_filename: doc?.original_filename || `${docType}.pdf`,
            content_type: doc?.content_type,
            doc_type: docType,
            doc_label: docType === 'visa' ? 'Visa Document' : docType === 'flight_ticket' ? 'Flight Ticket' : 'Offer Letter',
            _fetcher: () => api.get(`/worker/download/${docType}`, { responseType: 'blob' }),
        });
    };

    const toggleInterest = (s) => setFormInterests(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
    const toggleLanguage = (l) => setFormLanguages(prev => prev.includes(l) ? prev.filter(x => x !== l) : [...prev, l]);

    if (loading) return <div className="flex items-center justify-center h-[60vh]"><div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent animate-spin" /></div>;

    // ========== ONBOARDING ==========
    if (view === 'onboarding') {
        const totalSteps = 2;
        const progressPercent = ((Math.min(step, 2) - 1) / (totalSteps - 1)) * 100;
        const stepLabels = ['Profile', 'Passport'];

        return (
            <div className="max-w-lg mx-auto" data-testid="worker-onboarding">
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">Step {Math.min(step, totalSteps)} of {totalSteps}</span>
                        <span className="text-xs text-zinc-400">{Math.round(progressPercent)}%</span>
                    </div>
                    <Progress value={progressPercent} className="h-1 rounded-none" data-testid="onboarding-progress" />
                    <div className="flex justify-between mt-3">
                        {stepLabels.map((label, i) => (
                            <div key={i} className={`flex flex-col items-center gap-1 ${i + 1 <= step ? 'text-zinc-900' : 'text-zinc-300'}`}>
                                <div className={`w-8 h-8 flex items-center justify-center text-xs font-bold border-2 ${
                                    i + 1 < step ? 'bg-zinc-900 text-white border-zinc-900' :
                                    i + 1 === step ? 'border-zinc-900 text-zinc-900' : 'border-zinc-200 text-zinc-300'
                                }`}>{i + 1 < step ? <Check className="w-4 h-4" /> : i + 1}</div>
                                <span className="text-[10px] font-medium hidden sm:block">{label}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Step 1: Profile */}
                {step === 1 && (
                    <div className="fade-in" data-testid="onboarding-step-1">
                        <h2 className="font-heading text-2xl font-bold mb-1">Your Profile</h2>
                        <p className="text-sm text-zinc-500 mb-6">Tell employers about yourself</p>
                        <div className="space-y-4">
                            <div data-field="name">
                                <Label className="text-sm font-semibold">Full Name</Label>
                                <Input
                                    value={formName}
                                    onChange={e => { setFormName(e.target.value); clearError('name'); }}
                                    onBlur={() => { if (formName.trim()) clearError('name'); }}
                                    className={`mt-1.5 h-14 rounded-none text-base ${profileErrors.name ? 'border-2 border-red-500 focus:border-red-500' : (formName.trim() ? 'border-green-500' : '')}`}
                                    placeholder="Enter your full name"
                                    data-testid="worker-name-input"
                                    aria-invalid={!!profileErrors.name}
                                />
                                {profileErrors.name && <p className="text-xs text-red-600 mt-1" data-testid="error-name">{profileErrors.name}</p>}
                            </div>
                            <div data-field="dob">
                                <Label className="text-sm font-semibold">Date of Birth</Label>
                                <Input
                                    type="date"
                                    value={formDob}
                                    onChange={e => { setFormDob(e.target.value); clearError('dob'); }}
                                    className={`mt-1.5 h-14 rounded-none text-base ${profileErrors.dob ? 'border-2 border-red-500 focus:border-red-500' : (formDob ? 'border-green-500' : '')}`}
                                    data-testid="worker-dob-input"
                                    aria-invalid={!!profileErrors.dob}
                                />
                                {profileErrors.dob && <p className="text-xs text-red-600 mt-1" data-testid="error-dob">{profileErrors.dob}</p>}
                            </div>
                            <div data-field="country">
                                <Label className="text-sm font-semibold">Country</Label>
                                <Select value={formCountry} onValueChange={v => { setFormCountry(v); if (v !== 'Other') setFormCountryOther(''); clearError('country'); }}>
                                    <SelectTrigger
                                        className={`mt-1.5 h-14 rounded-none text-base ${profileErrors.country ? 'border-2 border-red-500' : (formCountry ? 'border-green-500' : '')}`}
                                        data-testid="worker-country-select"
                                        aria-invalid={!!profileErrors.country}
                                    ><SelectValue placeholder="Select your country" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Uganda">Uganda</SelectItem>
                                        <SelectItem value="Kenya">Kenya</SelectItem>
                                        <SelectItem value="Tanzania">Tanzania</SelectItem>
                                        <SelectItem value="Ethiopia">Ethiopia</SelectItem>
                                        <SelectItem value="Ghana">Ghana</SelectItem>
                                        <SelectItem value="Nigeria">Nigeria</SelectItem>
                                        <SelectItem value="Rwanda">Rwanda</SelectItem>
                                        <SelectItem value="Egypt">Egypt</SelectItem>
                                        <SelectItem value="Morocco">Morocco</SelectItem>
                                        <SelectItem value="Sudan">Sudan</SelectItem>
                                        <SelectItem value="Other">Other</SelectItem>
                                    </SelectContent>
                                </Select>
                                {formCountry === 'Other' && (
                                    <Input
                                        type="text"
                                        value={formCountryOther}
                                        onChange={e => { setFormCountryOther(e.target.value); clearError('country'); }}
                                        placeholder="Type your country"
                                        className={`mt-2 h-12 rounded-none text-base ${profileErrors.country ? 'border-2 border-red-500' : (formCountryOther ? 'border-green-500' : '')}`}
                                        data-testid="worker-country-other-input"
                                        maxLength={80}
                                    />
                                )}
                                {profileErrors.country && <p className="text-xs text-red-600 mt-1" data-testid="error-country">{profileErrors.country}</p>}
                            </div>
                            <div data-field="interests">
                                <Label className="text-sm font-semibold">Interests</Label>
                                <div className={`mt-1.5 space-y-3 max-h-64 overflow-y-auto border p-3 ${profileErrors.interests ? 'border-2 border-red-500' : (formInterests.length > 0 ? 'border-green-500' : 'border-zinc-200')}`}>
                                    {interestCategories.map(cat => (
                                        <div key={cat.id}>
                                            <p className="text-xs font-bold text-zinc-500 uppercase tracking-wide mb-1.5">{cat.name}</p>
                                            <div className="flex flex-wrap gap-1.5">{cat.roles.map(r => (<button key={r.id} type="button" onClick={() => { toggleInterest(r.name); clearError('interests'); }} className={`px-2.5 py-1.5 text-xs border transition-colors ${formInterests.includes(r.name) ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-200 hover:border-zinc-400'}`} data-testid={`interest-${r.name.toLowerCase().replace(/\s/g,'-')}`}>{r.name}</button>))}</div>
                                        </div>
                                    ))}
                                    {interestCategories.length === 0 && <p className="text-xs text-zinc-400">Loading interests...</p>}
                                </div>
                                {profileErrors.interests && <p className="text-xs text-red-600 mt-1" data-testid="error-interests">{profileErrors.interests}</p>}
                            </div>
                            <div data-field="languages">
                                <Label className="text-sm font-semibold">Languages</Label>
                                <div className={`flex flex-wrap gap-2 mt-1.5 ${profileErrors.languages ? 'border-2 border-red-500 p-2' : ''}`}>
                                    {LANGUAGES.map(l => (<button key={l} type="button" onClick={() => { toggleLanguage(l); clearError('languages'); }} className={`px-3 py-2 text-sm border transition-colors ${formLanguages.includes(l) ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-200 hover:border-zinc-400'}`} data-testid={`lang-${l.toLowerCase()}`}>{l}</button>))}
                                </div>
                                {profileErrors.languages && <p className="text-xs text-red-600 mt-1" data-testid="error-languages">{profileErrors.languages}</p>}
                            </div>
                            <div data-field="experience">
                                <Label className="text-sm font-semibold">Work Experience</Label>
                                <Input
                                    value={formExperience}
                                    onChange={e => { setFormExperience(e.target.value); clearError('experience'); }}
                                    onBlur={() => { if (formExperience.trim()) clearError('experience'); }}
                                    className={`mt-1.5 h-14 rounded-none text-base ${profileErrors.experience ? 'border-2 border-red-500 focus:border-red-500' : (formExperience.trim() ? 'border-green-500' : '')}`}
                                    placeholder="e.g., 3 years as security guard"
                                    data-testid="worker-experience-input"
                                    aria-invalid={!!profileErrors.experience}
                                />
                                {profileErrors.experience && <p className="text-xs text-red-600 mt-1" data-testid="error-experience">{profileErrors.experience}</p>}
                            </div>
                            <div><Label className="text-sm font-semibold">Phone Number</Label><Input value={formPhone} onChange={e => setFormPhone(e.target.value)} className="mt-1.5 h-14 rounded-none text-base" placeholder="+256..." data-testid="worker-phone-input" /><p className="text-xs text-zinc-400 mt-1">Optional — you can add this later.</p></div>
                            <Button onClick={saveProfile} disabled={saving} className="w-full h-14 rounded-none bg-zinc-900 text-white text-base font-semibold" data-testid="save-profile-btn">{saving ? 'Saving...' : 'Save & Continue'}<ChevronRight className="w-5 h-5 ml-2" /></Button>
                        </div>
                    </div>
                )}

                {/* Step 2: Passport — final required step. Interpol/Police Clearance has moved
                    to the optional checklist on the dashboard. */}
                {step === 2 && <UploadStep title="Upload Passport" desc="Scan or photo of your passport bio page" docType="passport" icon={<FileText className="w-8 h-8" />} onUpload={uploadDocument} saving={saving} testId="upload-passport" />}
            </div>
        );
    }

    // ========== DASHBOARD ==========
    const hasVideoFile = documents.some(d => d.doc_type === 'video_intro');
    const hasVideoLink = !!profile?.video_intro_url;
    const hasVideo = hasVideoFile || hasVideoLink;
    const hasMedical = documents.some(d => d.doc_type === 'medical_report');
    const hasPhoto = documents.some(d => d.doc_type === 'photo');
    const hasInterpolDoc = documents.some(d => d.doc_type === 'interpol');

    return (
        <div className="max-w-2xl mx-auto overflow-x-hidden" data-testid="worker-dashboard">
            <Tabs value={activeWorkerTab} onValueChange={changeWorkerTab} className="w-full">
                <TabsList className="grid grid-cols-3 w-full rounded-none h-12 bg-zinc-100 mb-6">
                    <TabsTrigger value="dashboard" className="rounded-none h-10 text-xs font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="worker-dashboard-tab">
                        <User className="w-4 h-4 mr-1" /> Dashboard
                    </TabsTrigger>
                    <TabsTrigger value="job-openings" className="rounded-none h-10 text-xs font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="worker-job-openings-tab">
                        <Briefcase className="w-4 h-4 mr-1" /> Job Openings
                    </TabsTrigger>
                    <TabsTrigger value="my-applications" className="rounded-none h-10 text-xs font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="worker-my-applications-tab">
                        <FileText className="w-4 h-4 mr-1" /> My Applications
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="dashboard">
            {paymentBanner && (
                <div
                    className={`border p-4 mb-6 flex items-start gap-3 ${
                        paymentBanner === 'success' ? 'border-green-300 bg-green-50' :
                        paymentBanner === 'error' ? 'border-red-300 bg-red-50' :
                        'border-blue-300 bg-blue-50'
                    }`}
                    data-testid={`payment-banner-${paymentBanner}`}
                >
                    {paymentBanner === 'success' ? <Check className="w-5 h-5 text-green-700 flex-shrink-0 mt-0.5" /> :
                     paymentBanner === 'error' ? <AlertCircle className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" /> :
                     <Clock className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />}
                    <div className="flex-1">
                        <p className={`text-sm font-semibold ${
                            paymentBanner === 'success' ? 'text-green-700' :
                            paymentBanner === 'error' ? 'text-red-700' : 'text-blue-700'
                        }`}>
                            {paymentBanner === 'success' ? 'Payment successful' :
                             paymentBanner === 'error' ? 'Payment issue' :
                             'Confirming payment...'}
                        </p>
                        <p className="text-xs text-zinc-600 mt-0.5">{paymentMessage}</p>
                    </div>
                    <button onClick={() => setPaymentBanner(null)} className="text-zinc-400 hover:text-zinc-700" data-testid="payment-banner-dismiss">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}
            <div className="border border-zinc-200 p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">Profile Status</span>
                    <Badge className="rounded-none bg-green-100 text-green-800 border-green-200" variant="outline">In Talent Pool</Badge>
                </div>
                <p className="text-sm text-zinc-600">Employers can see your profile and send you offers.</p>
                <ProfileCompletionBar profile={profile} documents={documents} />
            </div>

            {/* Optional Completion Checklist */}
            {(!hasPhoto || !hasMedical || !hasVideo || !hasInterpolDoc) && (
                <div className="border border-dashed border-zinc-300 p-4 mb-6" data-testid="optional-checklist">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-3">Complete Your Profile</p>
                    <div className="space-y-2">
                        {/* 1. Medical Report — highest priority */}
                        {!hasMedical && !appointment && (
                            <div className="p-2 border border-zinc-100" data-testid="optional-medical">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-zinc-400" /><span className="text-sm">Medical Report</span></div>
                                    <Badge variant="outline" className="rounded-none text-[11px] bg-amber-50 text-amber-700 border-amber-300" data-testid="medical-required-badge">Required</Badge>
                                </div>
                                <div className="flex gap-2 mt-2">
                                    <label className="cursor-pointer">
                                        <Button variant="outline" size="sm" className="rounded-none text-xs h-8" asChild><span><Upload className="w-3 h-3 mr-1" /> Upload Report</span></Button>
                                        <input type="file" className="hidden" accept=".pdf,image/*" onChange={e => e.target.files[0] && uploadDocument(e.target.files[0], 'medical_report')} data-testid="upload-medical-direct" />
                                    </label>
                                    <Button variant="outline" size="sm" className="rounded-none text-xs h-8" onClick={() => { loadMedicalPartners(); setShowMedicalBooking(true); }} data-testid="book-medical-btn">
                                        <Calendar className="w-3 h-3 mr-1" /> Book Appointment
                                    </Button>
                                </div>
                            </div>
                        )}
                        {appointment && appointment.status === 'confirmed' && (
                            <div className="p-2 border border-blue-100 bg-blue-50 flex items-center justify-between" data-testid="appointment-status">
                                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-blue-600" /><span className="text-sm text-blue-700">Medical appointment on {appointment.date} at {appointment.time}</span></div>
                                <Badge variant="outline" className="rounded-none text-xs text-blue-600 border-blue-200">Confirmed</Badge>
                            </div>
                        )}
                        {hasMedical && (
                            <div className="p-2 border border-green-100 bg-green-50 flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /><span className="text-sm text-green-700">Medical report uploaded</span></div>
                        )}
                        {/* 2. Video Introduction (LINK-based — replaces direct file upload) */}
                        <VideoIntroLinkRow
                            profile={profile}
                            hasVideoFile={hasVideoFile}
                            input={videoLinkInput}
                            setInput={setVideoLinkInput}
                            editing={videoLinkEditing}
                            setEditing={setVideoLinkEditing}
                            error={videoLinkError}
                            setError={setVideoLinkError}
                            saving={videoLinkSaving}
                            savedMsg={videoLinkSavedMsg}
                            onSave={saveVideoLink}
                            onRemove={removeVideoLink}
                        />
                        {/* 3. Profile Photo */}
                        {!hasPhoto && (
                            <label className="flex items-center justify-between p-2 border border-zinc-100 cursor-pointer hover:bg-zinc-50" data-testid="optional-photo-upload">
                                <div className="flex items-center gap-2"><Camera className="w-4 h-4 text-zinc-400" /><span className="text-sm">Profile Photo</span></div>
                                <Badge variant="outline" className="rounded-none text-[11px] bg-zinc-50 text-zinc-600 border-zinc-300" data-testid="photo-upload-badge">Upload</Badge>
                                <input type="file" className="hidden" accept="image/*" onChange={e => e.target.files[0] && uploadDocument(e.target.files[0], 'photo')} />
                            </label>
                        )}
                        {hasPhoto && <div className="p-2 border border-green-100 bg-green-50 flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /><span className="text-sm text-green-700">Profile photo uploaded</span></div>}
                        {/* 4. Interpol/Police Clearance — now optional */}
                        {!hasInterpolDoc && (
                            <label className="flex items-center justify-between p-2 border border-zinc-100 cursor-pointer hover:bg-zinc-50" data-testid="optional-interpol-upload">
                                <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-zinc-400" /><span className="text-sm">Interpol/Police Clearance</span></div>
                                <Badge variant="outline" className="rounded-none text-[11px] bg-zinc-50 text-zinc-600 border-zinc-300" data-testid="interpol-upload-badge">Upload</Badge>
                                <input type="file" className="hidden" accept=".pdf,image/*" onChange={e => e.target.files[0] && uploadDocument(e.target.files[0], 'interpol')} />
                            </label>
                        )}
                        {hasInterpolDoc && <div className="p-2 border border-green-100 bg-green-50 flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /><span className="text-sm text-green-700">Interpol/Police Clearance uploaded</span></div>}
                    </div>
                </div>
            )}

            {/* Phone Verification */}
            {!profile?.phone_verified && !otpVerified && (
                <div className="border border-zinc-200 p-4 mb-6" data-testid="phone-verification-section">
                    <div className="flex items-center gap-2 mb-3"><Smartphone className="w-5 h-5 text-zinc-500" /><h3 className="font-heading font-bold text-sm">Verify Phone Number</h3><Badge variant="outline" className="rounded-none text-xs text-amber-600 border-amber-200">Optional</Badge></div>
                    {otpError && <div className="text-xs text-red-600 mb-2 p-2 bg-red-50 border border-red-200">{otpError}</div>}
                    {!otpSent ? (
                        <div className="flex gap-2"><Input value={otpPhone} onChange={e => setOtpPhone(e.target.value)} placeholder="+256 7XX XXX XXX" className="h-12 rounded-none flex-1" data-testid="otp-phone-input" /><Button onClick={sendOtp} disabled={otpLoading || !otpPhone} className="h-12 rounded-none bg-zinc-900 text-white px-6" data-testid="send-otp-btn">{otpLoading ? 'Sending...' : 'Send Code'}</Button></div>
                    ) : (
                        <div className="space-y-2"><p className="text-xs text-zinc-500">Enter 6-digit code (check server logs)</p><div className="flex gap-2"><Input value={otpCode} onChange={e => setOtpCode(e.target.value)} placeholder="6-digit code" maxLength={6} className="h-12 rounded-none flex-1 text-center text-lg tracking-widest font-mono" data-testid="otp-code-input" /><Button onClick={verifyOtp} disabled={otpLoading || otpCode.length < 6} className="h-12 rounded-none bg-zinc-900 text-white px-6" data-testid="verify-otp-btn">{otpLoading ? '...' : 'Verify'}</Button></div></div>
                    )}
                </div>
            )}
            {(profile?.phone_verified || otpVerified) && (
                <div className="border border-green-200 bg-green-50 p-3 mb-6 flex items-center gap-2"><Phone className="w-4 h-4 text-green-600" /><span className="text-sm text-green-700 font-medium">Phone verified: {profile?.phone || otpPhone}</span><Check className="w-4 h-4 text-green-600" /></div>
            )}

            {/* Interviews — split into active (upcoming/live) and past (completed/missed/cancelled) */}
            {interviews.length > 0 && (() => {
                const now = Date.now();
                const GRACE_MS = 15 * 60 * 1000;
                // Resolve interview status using the new tracking fields.
                // Priority: cancelled > upcoming > live > completed > missed.
                const resolveStatus = (i) => {
                    if (i.status === 'cancelled') return 'cancelled';
                    if (!i.date || !i.time) return 'upcoming';
                    const startMs = new Date(`${i.date}T${i.time}`).getTime();
                    if (Number.isNaN(startMs)) return 'upcoming';
                    if (now < startMs) return 'upcoming';
                    if (now <= startMs + GRACE_MS) return 'live';
                    return i.join_clicked ? 'completed' : 'missed';
                };
                const withStatus = interviews.map(i => ({ ...i, _displayStatus: resolveStatus(i) }));
                const active = withStatus.filter(i => i._displayStatus === 'upcoming' || i._displayStatus === 'live');
                const past = withStatus.filter(i => ['completed', 'missed', 'cancelled'].includes(i._displayStatus));
                const handleJoinClick = (interviewId) => {
                    // Fire-and-forget — link opens regardless of API result
                    api.post(`/interviews/${interviewId}/join-clicked`).catch(() => {});
                };
                const STATUS_BADGE = {
                    upcoming: { label: 'Upcoming', cls: 'text-blue-700 border-blue-300 bg-blue-50' },
                    live: { label: 'Live Now', cls: 'text-green-700 border-green-400 bg-green-50 animate-pulse' },
                    completed: { label: 'Completed', cls: 'text-green-700 border-green-300' },
                    missed: { label: 'Missed', cls: 'text-red-600 border-red-300' },
                    cancelled: { label: 'Cancelled', cls: 'text-zinc-500 border-zinc-300' },
                };
                return (
                    <div id="interviews-section" className="mb-6 scroll-mt-20" data-testid="interviews-section">
                        <h3 className="font-heading text-lg font-bold mb-3 flex items-center gap-2"><Calendar className="w-5 h-5" /> Upcoming Interviews</h3>
                        {active.length === 0 ? (
                            <p className="text-sm text-zinc-500 mb-3" data-testid="no-active-interviews">No upcoming interviews scheduled.</p>
                        ) : (
                            <div className="space-y-3">
                                {active.map(i => {
                                    const link = i.meet_link || i.zoom_link;
                                    const s = i._displayStatus;
                                    const badge = STATUS_BADGE[s];
                                    const cardCls = s === 'live'
                                        ? 'border-green-300 bg-green-50 p-4'
                                        : 'border-blue-200 bg-blue-50 p-4';
                                    return (
                                        <div key={i.id} className={`border ${cardCls}`} data-testid={`interview-${i.id}`}>
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="font-semibold text-sm">{i.employer_name} {i.company_name && `(${i.company_name})`}</span>
                                                <Badge variant="outline" className={`rounded-none text-xs ${badge.cls}`} data-testid={`interview-status-${i.id}`}>{badge.label}</Badge>
                                            </div>
                                            <div className="flex items-center gap-4 text-sm text-zinc-600 mb-2">
                                                <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {i.date}</span>
                                                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {i.time}</span>
                                            </div>
                                            {link && (
                                                <a
                                                    href={link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={() => handleJoinClick(i.id)}
                                                    className="inline-flex items-center gap-1 text-sm text-blue-700 font-semibold hover:underline"
                                                    data-testid={`meet-link-${i.id}`}
                                                >
                                                    <ExternalLink className="w-3.5 h-3.5" /> Join Interview
                                                </a>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {past.length > 0 && (
                            <div className="mt-4 border-t border-zinc-100 pt-3" data-testid="past-interviews-section">
                                <button
                                    type="button"
                                    onClick={() => setShowPastInterviews(v => !v)}
                                    className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 hover:text-zinc-900"
                                    data-testid="past-interviews-toggle"
                                >
                                    {showPastInterviews ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    Past Interviews ({past.length})
                                </button>
                                {showPastInterviews && (
                                    <div className="space-y-2 mt-3">
                                        {past.map(i => {
                                            const link = i.meet_link || i.zoom_link;
                                            const s = i._displayStatus;
                                            const badge = STATUS_BADGE[s];
                                            return (
                                                <div key={i.id} className="border border-zinc-200 bg-zinc-50 p-3" data-testid={`past-interview-${i.id}`}>
                                                    <div className="flex items-center justify-between mb-1">
                                                        <span className="font-semibold text-sm text-zinc-700">{i.employer_name} {i.company_name && `(${i.company_name})`}</span>
                                                        <Badge variant="outline" className={`rounded-none text-xs ${badge.cls}`} data-testid={`past-interview-label-${i.id}`}>
                                                            {badge.label}
                                                        </Badge>
                                                    </div>
                                                    <div className="flex items-center gap-4 text-xs text-zinc-500 mb-1">
                                                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {i.date}</span>
                                                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {i.time}</span>
                                                    </div>
                                                    {link && s !== 'cancelled' && (
                                                        <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-700 hover:underline" data-testid={`past-meet-link-${i.id}`}>
                                                            <ExternalLink className="w-3 h-3" /> Meet link
                                                        </a>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* Active Offer */}
            {activeOffer && (
                <div id="offers-section" className="border border-zinc-900 p-6 mb-6 scroll-mt-20" data-testid="active-offer-section">
                    <div className="flex items-center gap-2 mb-4"><Briefcase className="w-5 h-5" /><h3 className="font-heading text-lg font-bold">Active Offer</h3></div>
                    <div className="space-y-3">
                        <div className="flex justify-between"><span className="text-sm text-zinc-500">Position</span><span className="text-sm font-semibold">{activeOffer.title}</span></div>
                        <div className="flex justify-between"><span className="text-sm text-zinc-500">Salary</span><span className="text-sm font-semibold">{activeOffer.currency || 'USD'} {activeOffer.salary}</span></div>
                        <div className="flex justify-between"><span className="text-sm text-zinc-500">Location</span><span className="text-sm font-semibold">{activeOffer.location}, {activeOffer.country}</span></div>
                        <div className="flex justify-between"><span className="text-sm text-zinc-500">Benefits</span><span className="text-sm font-semibold">{activeOffer.benefits}</span></div>
                        {activeOffer.description && <div><span className="text-xs text-zinc-400 uppercase tracking-wide font-bold">Description</span><p className="text-sm mt-1">{activeOffer.description}</p></div>}
                        <div className="flex justify-between"><span className="text-sm text-zinc-500">Employer</span><span className="text-sm font-semibold">{activeOffer.employer_name}</span></div>
                        <Separator />
                        <div id="visa-tracking-section" className="scroll-mt-20"><p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-3">Visa Progress</p>
                            {/* Informational timeline message — shown while visa is in progress (none/submitted/processing)
                                and the worker hasn't paid yet. Hidden once status reaches approved (the payment prompt below takes over). */}
                            {(() => {
                                const status = activeOffer.visa_status || 'none';
                                if (profile?.document_access_paid) return null;
                                if (status === 'approved' || status === 'rejected') return null;
                                const MESSAGES = {
                                    none: {
                                        heading: 'Your employer is preparing your application',
                                        body: 'Your employer is now working on your visa application. This process typically takes 4 to 12 weeks depending on your destination country and the type of work visa being processed. You will receive a notification at every stage. In the meantime, make sure all your profile documents are complete and up to date.',
                                    },
                                    submitted: {
                                        heading: 'Your application has been submitted',
                                        body: 'Your visa application is now in the hands of the immigration authorities. Processing times vary by country; most Gulf work visas take between 2 to 8 weeks from submission to a decision. We will notify you as soon as there is an update. No action is needed from you right now.',
                                    },
                                    processing: {
                                        heading: 'Your application is being reviewed',
                                        body: 'Your visa application is actively being reviewed by immigration authorities. You are getting close; this stage typically takes 1 to 4 weeks. Stay patient and make sure you are reachable on your registered contact details in case your employer needs anything from you.',
                                    },
                                };
                                const m = MESSAGES[status] || MESSAGES.none;
                                return (
                                    <div className="mb-4 flex gap-3 p-3 sm:p-4 border border-blue-200 bg-blue-50 rounded-sm" data-testid={`visa-timeline-${status}`}>
                                        <AlertCircle className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
                                        <div className="min-w-0">
                                            <p className="font-heading font-bold text-sm text-blue-900 mb-1">{m.heading}</p>
                                            <p className="text-xs sm:text-sm text-blue-900/80 leading-relaxed">{m.body}</p>
                                        </div>
                                    </div>
                                );
                            })()}
                            <div className="space-y-3">{['submitted', 'processing', 'approved'].map((s, i) => {
                                const statuses = ['submitted', 'processing', 'approved']; const currentIdx = statuses.indexOf(activeOffer.visa_status); const isComplete = i <= currentIdx;
                                return (<div key={s} className="flex items-center gap-3" data-testid={`visa-step-${s}`}><div className={`w-8 h-8 flex items-center justify-center border-2 flex-shrink-0 ${isComplete ? 'bg-zinc-900 border-zinc-900 text-white' : 'border-zinc-200 text-zinc-300'}`}>{isComplete ? <Check className="w-4 h-4" /> : <span className="text-xs">{i + 1}</span>}</div><span className={`text-sm capitalize ${isComplete ? 'font-bold' : 'text-zinc-300'}`}>{s}</span></div>);
                            })}</div>
                        </div>
                        {activeOffer.visa_status === 'approved' && !profile?.document_access_paid && (activeOffer.visa_doc_id || activeOffer.ticket_doc_id) && (
                            <div className="mt-4 border border-zinc-900 p-4 bg-zinc-50" data-testid="payment-section">
                                <div className="flex items-center gap-2 mb-2"><CreditCard className="w-5 h-5" /><h4 className="font-heading font-bold">Your visa has been approved</h4></div>
                                {feeError ? (
                                    <p className="text-sm text-red-600 mb-3" data-testid="payment-fee-error">Unable to load payment details. Please refresh the page or contact support.</p>
                                ) : (
                                    <p className="text-sm text-zinc-600 mb-3">To access and download your visa and flight ticket, a one-time document access fee of <span className="font-bold" data-testid="payment-fee-amount">{formatFee(feeUsd)}</span> USD is required.</p>
                                )}
                                <Button
                                    onClick={() => setPaymentModalOpen(true)}
                                    disabled={feeError}
                                    className="w-full h-14 rounded-none bg-zinc-900 text-white font-semibold"
                                    data-testid="pay-btn"
                                >
                                    Pay {formatFee(feeUsd)} now
                                </Button>
                                <p className="text-xs text-zinc-400 mt-2 text-center">Secured by Stripe.</p>
                            </div>
                        )}
                        {/* Documents — locked or unlocked based on document_access_paid */}
                        {activeOffer.visa_status === 'approved' && (visaDoc || ticketDoc || activeOffer.visa_doc_id || activeOffer.ticket_doc_id) && (
                            <div id="documents-section" className="mt-4 space-y-3 scroll-mt-20" data-testid="download-docs-section">
                                <div className="flex items-center gap-2">
                                    {profile?.document_access_paid
                                        ? <><Download className="w-5 h-5 text-green-600" /><h4 className="font-heading font-bold text-green-700">Documents Unlocked</h4></>
                                        : <><Lock className="w-5 h-5 text-zinc-500" /><h4 className="font-heading font-bold text-zinc-600">Documents Locked</h4></>}
                                </div>
                                {(visaDoc || activeOffer.visa_doc_id) && (
                                    <div className={`flex flex-wrap items-center justify-between gap-2 p-3 border ${profile?.document_access_paid ? 'border-green-200 bg-green-50' : 'border-zinc-200 bg-zinc-50'}`} data-testid="visa-doc-row">
                                        <div className="flex items-center gap-2 min-w-0">
                                            {profile?.document_access_paid ? <FileText className="w-4 h-4 text-green-700 flex-shrink-0" /> : <Lock className="w-4 h-4 text-zinc-400 flex-shrink-0" />}
                                            <span className={`text-sm font-medium truncate ${profile?.document_access_paid ? '' : 'text-zinc-500'}`}>Visa Document</span>
                                        </div>
                                        <div className="flex gap-2 flex-shrink-0">
                                            <Button
                                                size="sm" variant="outline"
                                                className={`rounded-none text-xs h-8 px-2 sm:px-3 ${profile?.document_access_paid ? 'border-green-300 text-green-700' : 'border-zinc-300 text-zinc-400 cursor-not-allowed'}`}
                                                onClick={() => profile?.document_access_paid && openProtectedPreview('visa', visaDoc)}
                                                disabled={!profile?.document_access_paid}
                                                title={profile?.document_access_paid ? 'View visa' : 'Complete payment to unlock your documents'}
                                                aria-label="View visa"
                                                data-testid="view-visa-btn">
                                                <Eye className="w-3 h-3 sm:mr-1" /> <span className="hidden sm:inline">View</span>
                                            </Button>
                                            <Button
                                                size="sm" variant="outline"
                                                className={`rounded-none text-xs h-8 px-2 sm:px-3 ${profile?.document_access_paid ? 'border-green-300 text-green-700' : 'border-zinc-300 text-zinc-400 cursor-not-allowed'}`}
                                                onClick={() => profile?.document_access_paid && handleDownload('visa')}
                                                disabled={!profile?.document_access_paid}
                                                title={profile?.document_access_paid ? 'Download visa' : 'Complete payment to unlock your documents'}
                                                aria-label="Download visa"
                                                data-testid="download-visa-btn">
                                                <Download className="w-3 h-3 sm:mr-1" /> <span className="hidden sm:inline">Download</span>
                                            </Button>
                                            <Button
                                                size="sm" variant="ghost"
                                                className={`rounded-none text-xs h-8 px-2 sm:px-3 ${profile?.document_access_paid ? 'text-green-700' : 'text-zinc-400 cursor-not-allowed'}`}
                                                onClick={() => profile?.document_access_paid && handlePrint('visa')}
                                                disabled={!profile?.document_access_paid}
                                                title={profile?.document_access_paid ? 'Print visa' : 'Complete payment to unlock your documents'}
                                                aria-label="Print visa"
                                                data-testid="print-visa-btn">
                                                <Printer className="w-3 h-3 sm:mr-1" /> <span className="hidden sm:inline">Print</span>
                                            </Button>
                                        </div>
                                    </div>
                                )}
                                {(ticketDoc || activeOffer.ticket_doc_id) && (
                                    <div className={`flex flex-wrap items-center justify-between gap-2 p-3 border ${profile?.document_access_paid ? 'border-green-200 bg-green-50' : 'border-zinc-200 bg-zinc-50'}`} data-testid="ticket-doc-row">
                                        <div className="flex items-center gap-2 min-w-0">
                                            {profile?.document_access_paid ? <FileText className="w-4 h-4 text-green-700 flex-shrink-0" /> : <Lock className="w-4 h-4 text-zinc-400 flex-shrink-0" />}
                                            <span className={`text-sm font-medium truncate ${profile?.document_access_paid ? '' : 'text-zinc-500'}`}>Flight Ticket</span>
                                        </div>
                                        <div className="flex gap-2 flex-shrink-0">
                                            <Button size="sm" variant="outline"
                                                className={`rounded-none text-xs h-8 px-2 sm:px-3 ${profile?.document_access_paid ? 'border-green-300 text-green-700' : 'border-zinc-300 text-zinc-400 cursor-not-allowed'}`}
                                                onClick={() => profile?.document_access_paid && openProtectedPreview('flight_ticket', ticketDoc)}
                                                disabled={!profile?.document_access_paid}
                                                title={profile?.document_access_paid ? 'View flight ticket' : 'Complete payment to unlock your documents'}
                                                aria-label="View flight ticket"
                                                data-testid="view-ticket-btn">
                                                <Eye className="w-3 h-3 sm:mr-1" /> <span className="hidden sm:inline">View</span>
                                            </Button>
                                            <Button size="sm" variant="outline"
                                                className={`rounded-none text-xs h-8 px-2 sm:px-3 ${profile?.document_access_paid ? 'border-green-300 text-green-700' : 'border-zinc-300 text-zinc-400 cursor-not-allowed'}`}
                                                onClick={() => profile?.document_access_paid && handleDownload('flight_ticket')}
                                                disabled={!profile?.document_access_paid}
                                                title={profile?.document_access_paid ? 'Download ticket' : 'Complete payment to unlock your documents'}
                                                aria-label="Download flight ticket"
                                                data-testid="download-ticket-btn">
                                                <Download className="w-3 h-3 sm:mr-1" /> <span className="hidden sm:inline">Download</span>
                                            </Button>
                                            <Button size="sm" variant="ghost"
                                                className={`rounded-none text-xs h-8 px-2 sm:px-3 ${profile?.document_access_paid ? 'text-green-700' : 'text-zinc-400 cursor-not-allowed'}`}
                                                onClick={() => profile?.document_access_paid && handlePrint('flight_ticket')}
                                                disabled={!profile?.document_access_paid}
                                                title={profile?.document_access_paid ? 'Print ticket' : 'Complete payment to unlock your documents'}
                                                aria-label="Print flight ticket"
                                                data-testid="print-ticket-btn">
                                                <Printer className="w-3 h-3 sm:mr-1" /> <span className="hidden sm:inline">Print</span>
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {offerLetterDoc && (
                            <div className="mt-4" data-testid="offer-letter-section">
                                <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-2">Official Offer Letter</p>
                                <div className="flex items-center justify-between p-3 border border-zinc-200 bg-zinc-50">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <FileText className="w-4 h-4 text-zinc-700 flex-shrink-0" />
                                        <span className="text-sm font-medium truncate">{offerLetterDoc.original_filename || 'Offer Letter'}</span>
                                    </div>
                                    <Button size="sm" variant="outline" className="rounded-none text-xs h-8 border-zinc-900" onClick={() => setPreviewDoc(offerLetterDoc)} data-testid="view-offer-letter-btn">
                                        <Eye className="w-3 h-3 mr-1" /> View
                                    </Button>
                                </div>
                            </div>
                        )}
                        {activeOffer.visa_status === 'none' && <div className="flex items-center gap-2 text-zinc-400 text-sm"><Clock className="w-4 h-4" /><span>Waiting for employer to start visa process</span></div>}
                    </div>
                </div>
            )}

            {/* Pending Offers */}
            {!activeOffer && offers.filter(o => o.status === 'pending').length > 0 && (
                <div className="mb-6" data-testid="pending-offers-section">
                    <h3 className="font-heading text-lg font-bold mb-4">Job Offers</h3>
                    <div className="space-y-3">
                        {offers.filter(o => o.status === 'pending').map(offer => (
                            <div key={offer.id} className="border border-zinc-200 p-5 hover:border-zinc-800 transition-colors" data-testid={`offer-${offer.id}`}>
                                <div className="flex items-start justify-between mb-3"><div><h4 className="font-semibold text-base">{offer.title}</h4><p className="text-sm text-zinc-500">{offer.employer_name} {offer.company_name && `(${offer.company_name})`}</p></div><Badge variant="outline" className="rounded-none">Pending</Badge></div>
                                <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                                    <div className="flex items-center gap-1.5 text-zinc-500"><span className="text-xs font-bold">{offer.currency || 'USD'}</span><span>{offer.salary}</span></div>
                                    <div className="flex items-center gap-1.5 text-zinc-500"><MapPin className="w-3.5 h-3.5" /><span>{offer.location}, {offer.country}</span></div>
                                </div>
                                {offer.benefits && <p className="text-sm text-zinc-600 mb-2"><span className="font-semibold text-xs text-zinc-400">Benefits:</span> {offer.benefits}</p>}
                                {offer.description && <div className="bg-zinc-50 p-3 mb-4 border border-zinc-100"><p className="text-xs text-zinc-400 uppercase tracking-wide font-bold mb-1">Job Description</p><p className="text-sm text-zinc-700 leading-relaxed">{offer.description}</p></div>}
                                <div className="flex gap-3">
                                    <Button onClick={() => respondToOffer(offer.id, 'accept')} className="flex-1 h-12 rounded-none bg-zinc-900 text-white font-semibold" data-testid={`accept-offer-${offer.id}`}><Check className="w-4 h-4 mr-1" /> Accept</Button>
                                    <Button onClick={() => respondToOffer(offer.id, 'reject')} variant="outline" className="flex-1 h-12 rounded-none border-zinc-300" data-testid={`reject-offer-${offer.id}`}><X className="w-4 h-4 mr-1" /> Decline</Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!activeOffer && offers.filter(o => o.status === 'pending').length === 0 && (
                <div className="text-center py-12 border border-zinc-200" data-testid="no-offers-state">
                    <Briefcase className="w-12 h-12 text-zinc-200 mx-auto mb-4" />
                    <h3 className="font-heading text-lg font-bold mb-2">No Offers Yet</h3>
                    <p className="text-sm text-zinc-500 max-w-sm mx-auto">No job offers yet. Make sure your profile is complete and all required documents are uploaded to appear in the talent pool.</p>
                </div>
            )}

            {/* Additional Documents (Fix 3) */}
            <div className="border border-zinc-200 p-5 mt-6" data-testid="additional-documents-section">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-zinc-500" />
                        <h3 className="font-heading font-bold text-sm">Additional Documents</h3>
                        <Badge variant="outline" className="rounded-none text-xs">Optional</Badge>
                    </div>
                </div>
                <p className="text-xs text-zinc-500 mb-4">Strengthen your profile by adding CVs, certificates, recommendation letters, or previous work contracts. Max 10 MB per file.</p>

                <div className="space-y-2 mb-4">
                    <Label className="text-xs font-semibold">Document Label</Label>
                    <Input
                        value={additionalLabel}
                        onChange={e => setAdditionalLabel(e.target.value)}
                        placeholder="e.g., Forklift Operator Certificate"
                        className="h-11 rounded-none"
                        data-testid="additional-label-input"
                    />
                    <label className={`upload-zone flex items-center justify-center p-4 cursor-pointer ${!additionalLabel.trim() ? 'opacity-50 pointer-events-none' : ''}`} data-testid="additional-upload-zone">
                        {uploadingAdditional ? (
                            <span className="text-sm text-zinc-500">Uploading...</span>
                        ) : (
                            <><Upload className="w-4 h-4 text-zinc-400 mr-2" /><span className="text-sm text-zinc-500">Select file to upload</span></>
                        )}
                        <input
                            type="file"
                            className="hidden"
                            accept=".pdf,image/*,.doc,.docx"
                            disabled={uploadingAdditional || !additionalLabel.trim()}
                            onChange={e => e.target.files[0] && uploadAdditional(e.target.files[0])}
                            data-testid="additional-upload-input"
                        />
                    </label>
                    {additionalError && <p className="text-xs text-red-600" data-testid="additional-error">{additionalError}</p>}
                </div>

                <div className="space-y-2">
                    {documents.filter(d => d.doc_type === 'additional').length === 0 ? (
                        <p className="text-xs text-zinc-400 italic">No additional documents uploaded yet.</p>
                    ) : (
                        documents.filter(d => d.doc_type === 'additional').map(doc => (
                            <div key={doc.id} className="flex items-center justify-between p-3 border border-zinc-200" data-testid={`additional-doc-${doc.id}`}>
                                <div className="flex items-center gap-2 min-w-0">
                                    <FileText className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium truncate">{doc.doc_label || 'Additional document'}</p>
                                        <p className="text-[10px] text-zinc-400 truncate">{doc.original_filename}</p>
                                    </div>
                                </div>
                                <div className="flex gap-1 flex-shrink-0">
                                    <Button size="sm" variant="outline" className="rounded-none text-xs h-8 border-zinc-900" onClick={() => setPreviewDoc(doc)} data-testid={`view-additional-${doc.id}`}>
                                        <Eye className="w-3 h-3 mr-1" /> View
                                    </Button>
                                    <label className="cursor-pointer">
                                        <Button size="sm" variant="ghost" className="rounded-none text-xs h-8" asChild>
                                            <span><Upload className="w-3 h-3 mr-1" /> Replace</span>
                                        </Button>
                                        <input
                                            type="file"
                                            className="hidden"
                                            accept=".pdf,image/*,.doc,.docx"
                                            data-testid={`replace-additional-${doc.id}`}
                                            onChange={async e => {
                                                const f = e.target.files[0];
                                                if (!f) return;
                                                if (f.size > 10 * 1024 * 1024) { setAdditionalError('File exceeds 10 MB limit'); return; }
                                                await deleteAdditional(doc.id);
                                                setAdditionalLabel(doc.doc_label || '');
                                                const fd = new FormData();
                                                fd.append('file', f);
                                                fd.append('doc_type', 'additional');
                                                fd.append('doc_label', doc.doc_label || '');
                                                setUploadingAdditional(true);
                                                try {
                                                    await api.post('/worker/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                                    await fetchData();
                                                    setAdditionalLabel('');
                                                } catch (err) {
                                                    setAdditionalError(err.response?.data?.detail || 'Replace failed');
                                                } finally {
                                                    setUploadingAdditional(false);
                                                }
                                            }}
                                        />
                                    </label>
                                    <Button size="sm" variant="ghost" className="rounded-none text-xs h-8 text-red-500 hover:text-red-700" onClick={() => deleteAdditional(doc.id)} data-testid={`delete-additional-${doc.id}`}>
                                        <Trash2 className="w-3 h-3" />
                                    </Button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Email Preferences — opt out of categories of transactional emails. */}
            <div className="mt-6">
                <EmailPreferencesPanel />
            </div>
                </TabsContent>

                <TabsContent value="job-openings">
                    <WorkerJobBoard
                        profile={profile}
                        documents={documents}
                        hasAcceptedOffer={!!activeOffer}
                        onNavigateToMyApplications={() => changeWorkerTab('my-applications')}
                    />
                </TabsContent>

                <TabsContent value="my-applications">
                    <WorkerMyApplications
                        refreshToken={activeWorkerTab === 'my-applications' ? 1 : 0}
                        onNavigateToOffers={() => changeWorkerTab('dashboard')}
                    />
                </TabsContent>
            </Tabs>

            {/* Universal Document Preview Modal (Fix 5) */}
            <DocumentPreviewModal
                open={!!previewDoc}
                onClose={() => setPreviewDoc(null)}
                doc={previewDoc}
            />
            <DocumentPreviewModal
                open={!!previewMeta}
                onClose={() => setPreviewMeta(null)}
                doc={previewMeta}
                directBlobFetcher={previewMeta?._fetcher}
            />

            {/* Stripe Payment Modal (Document Access Fee) */}
            <StripePaymentModal
                open={paymentModalOpen}
                onClose={() => setPaymentModalOpen(false)}
                onPaid={() => { setPaymentModalOpen(false); onPaidSuccess(); }}
            />

            {/* Medical Booking Dialog */}
            {showMedicalBooking && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="medical-booking-modal">
                    <div className="bg-white border-2 border-zinc-900 p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-heading text-xl font-bold">Book Medical Appointment</h3>
                            <button onClick={() => { setShowMedicalBooking(false); setBookingStep('partners'); setSelectedSlot(null); setSelectedPartner(null); }} className="text-zinc-400 hover:text-zinc-900" data-testid="close-booking-modal"><X className="w-5 h-5" /></button>
                        </div>

                        {bookingStep === 'partners' && (
                            <div data-testid="booking-partners-list">
                                {medPartners.length === 0 ? (
                                    <div className="text-center py-8"><AlertCircle className="w-8 h-8 text-zinc-300 mx-auto mb-3" /><p className="text-sm text-zinc-500">No partners available at this time.</p><Button onClick={loadMedicalPartners} variant="outline" className="mt-3 rounded-none" data-testid="refresh-partners-btn">Refresh</Button></div>
                                ) : (
                                    <div className="space-y-3">{medPartners.filter(p => p.available_slots > 0).map(p => (
                                        <button key={p.id} onClick={() => loadPartnerSlots(p.id)} className="w-full border border-zinc-200 p-4 flex items-center justify-between hover:border-zinc-800 transition-colors text-left" data-testid={`partner-${p.id}`}>
                                            <div className="flex items-center gap-3"><div className="w-10 h-10 bg-zinc-100 flex items-center justify-center"><User className="w-5 h-5 text-zinc-400" /></div><div><p className="font-semibold text-sm">{p.name}</p><p className="text-xs text-zinc-500">{p.clinic_name || 'Medical Partner'}</p></div></div>
                                            <Badge variant="outline" className="rounded-none text-xs">{p.available_slots} slot{p.available_slots !== 1 ? 's' : ''}</Badge>
                                        </button>
                                    ))}</div>
                                )}
                            </div>
                        )}

                        {bookingStep === 'slots' && selectedPartner && (
                            <div data-testid="booking-slots-list">
                                <button onClick={() => { setBookingStep('partners'); setSelectedSlot(null); }} className="text-xs text-zinc-500 mb-4 flex items-center gap-1 hover:text-zinc-900"><ChevronRight className="w-3 h-3 rotate-180" /> Back</button>
                                <div className="border border-zinc-200 p-3 mb-4"><p className="font-semibold text-sm">{selectedPartner.name}</p></div>
                                {partnerSlots.length === 0 ? (<p className="text-sm text-zinc-500 text-center py-4">No available slots.</p>) : (
                                    <div className="space-y-2">{Object.entries(partnerSlots.reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {})).map(([date, slots]) => (
                                        <div key={date} className="border border-zinc-200 p-3"><p className="text-sm font-semibold mb-2">{new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                                            <div className="flex flex-wrap gap-2">{slots.map(s => (<button key={s.id} onClick={() => { setSelectedSlot(s); setBookingStep('confirm'); }} className="px-3 py-1.5 text-sm border border-zinc-200 hover:border-zinc-800" data-testid={`slot-${s.id}`}>{s.time}</button>))}</div></div>
                                    ))}</div>
                                )}
                            </div>
                        )}

                        {bookingStep === 'confirm' && selectedPartner && selectedSlot && (
                            <div data-testid="booking-confirm">
                                <button onClick={() => setBookingStep('slots')} className="text-xs text-zinc-500 mb-4 flex items-center gap-1 hover:text-zinc-900"><ChevronRight className="w-3 h-3 rotate-180" /> Back</button>
                                <div className="space-y-3 mb-6">
                                    <div className="flex justify-between text-sm"><span className="text-zinc-500">Partner</span><span className="font-semibold">{selectedPartner.name}</span></div>
                                    <div className="flex justify-between text-sm"><span className="text-zinc-500">Date</span><span className="font-semibold">{selectedSlot.date}</span></div>
                                    <div className="flex justify-between text-sm"><span className="text-zinc-500">Time</span><span className="font-semibold">{selectedSlot.time}</span></div>
                                    <Separator />
                                    <div className="flex justify-between text-sm"><span className="text-zinc-500">Appointment Fee</span><span className="font-heading font-bold text-lg">${selectedPartner.appointment_fee || 120}</span></div>
                                </div>
                                <Button onClick={async () => { await bookAppointment(); setShowMedicalBooking(false); }} disabled={bookingLoading} className="w-full h-14 rounded-none bg-zinc-900 text-white text-base font-semibold" data-testid="confirm-booking-btn">{bookingLoading ? 'Processing...' : 'Pay & Confirm'}<CreditCard className="w-5 h-5 ml-2" /></Button>
                                <p className="text-xs text-zinc-400 mt-2 text-center">MOCK payment</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function UploadStep({ title, desc, docType, icon, onUpload, saving, accept, testId }) {
    const [file, setFile] = useState(null);
    const [error, setError] = useState('');
    const handleSubmit = () => {
        if (!file) {
            setError(docType === 'passport' ? 'Please upload your passport' : `Please upload your ${title.toLowerCase()}`);
            return;
        }
        setError('');
        onUpload(file, docType);
    };
    return (
        <div className="fade-in" data-testid={`onboarding-${testId}`}>
            <h2 className="font-heading text-2xl font-bold mb-1">{title}</h2>
            <p className="text-sm text-zinc-500 mb-6">{desc}</p>
            <label className={`upload-zone flex flex-col items-center justify-center p-8 mb-4 ${file ? 'has-file' : ''} ${error ? 'border-2 border-red-500' : ''}`} data-field="upload">
                {file ? (<><Check className="w-8 h-8 text-green-600 mb-2" /><span className="text-sm font-medium text-green-700">{file.name}</span><span className="text-xs text-zinc-400 mt-1">{(file.size / 1024).toFixed(1)} KB</span></>) : (<><div className="text-zinc-300 mb-3">{icon}</div><span className="text-sm font-medium text-zinc-500">Tap to select file</span></>)}
                <input type="file" className="hidden" onChange={e => { const f = e.target.files[0]; if (f) { setFile(f); setError(''); } }} accept={accept || "image/*,.pdf"} data-testid={`${testId}-input`} />
            </label>
            {error && <p className="text-xs text-red-600 mb-3" data-testid={`${testId}-error`}>{error}</p>}
            <Button onClick={handleSubmit} disabled={saving} className="w-full h-14 rounded-none bg-zinc-900 text-white text-base font-semibold" data-testid={`${testId}-submit`}>{saving ? 'Uploading...' : 'Upload & Continue'}<Upload className="w-5 h-5 ml-2" /></Button>
        </div>
    );
}

function ProfileCompletionBar({ profile, documents }) {
    if (!profile) return null;
    const docs = documents || [];
    const has = (type) => docs.some(d => d.doc_type === type);
    // Required fields — what a worker MUST complete to be visible in the talent pool.
    // Interpol/Police Clearance and Profile Photo are now optional (no DB rules block on them).
    const required = [
        { key: 'name', label: 'Full name', done: !!profile.name },
        { key: 'dob', label: 'Date of birth', done: !!profile.dob },
        { key: 'country', label: 'Country', done: !!profile.country },
        { key: 'interests', label: 'Interests', done: Array.isArray(profile.interests) && profile.interests.length > 0 },
        { key: 'languages', label: 'Languages', done: Array.isArray(profile.languages) && profile.languages.length > 0 },
        { key: 'experience', label: 'Work experience', done: !!profile.experience },
        { key: 'passport', label: 'Passport', done: has('passport') },
    ];
    const optional = [
        { key: 'medical', label: 'Medical report', done: has('medical_report') },
        { key: 'photo', label: 'Profile photo', done: has('photo') },
        { key: 'video', label: 'Video introduction', done: has('video_intro') },
        { key: 'interpol', label: 'Interpol/Police Clearance', done: has('interpol') },
        { key: 'additional', label: 'Additional documents', done: docs.some(d => d.doc_type === 'additional') },
        { key: 'phone', label: 'Phone verified', done: !!profile.phone_verified },
        { key: 'email', label: 'Email verified', done: !!profile.email_verified },
    ];
    const reqDone = required.filter(r => r.done).length;
    const optDone = optional.filter(o => o.done).length;
    const total = required.length + optional.length;
    const pct = Math.round(((reqDone + optDone) / total) * 100);
    const reqComplete = reqDone === required.length;
    // Fix 4: Medical Report must always lead the "Still needed" list when it
    // hasn't been uploaded yet, even when required items remain.
    const missingRequired = required.filter(r => !r.done).map(r => r.label);
    const medicalMissing = !has('medical_report');
    const missing = medicalMissing
        ? ['Medical report', ...missingRequired]
        : missingRequired;
    const remainingOptional = optional
        .filter(o => !o.done && o.key !== 'medical')  // medical surfaced above when missing
        .map(o => o.label);

    return (
        <div className="mt-4 pt-4 border-t border-zinc-100" data-testid="profile-completion">
            <div className="flex items-center justify-between mb-2">
                <span className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-500">Profile {pct}% complete</span>
                {reqComplete && <Badge className="rounded-none bg-green-100 text-green-700 border-green-300" data-testid="profile-active-badge">Active in talent pool</Badge>}
            </div>
            <div className="w-full h-2 bg-zinc-100 overflow-hidden">
                <div
                    className={`h-full transition-all ${reqComplete ? 'bg-green-600' : 'bg-zinc-900'}`}
                    style={{ width: `${pct}%` }}
                    data-testid="profile-progress-bar"
                />
            </div>
            {missing.length > 0 && (
                <p className="text-xs text-zinc-500 mt-2" data-testid="profile-missing-required">
                    <span className="font-semibold text-zinc-700">Still needed:</span> {missing.join(', ')}
                </p>
            )}
            {reqComplete && !medicalMissing && remainingOptional.length > 0 && (
                <p className="text-xs text-zinc-500 mt-2" data-testid="profile-strengthen">
                    <span className="font-semibold text-zinc-700">Strengthen your profile:</span> {remainingOptional.slice(0, 3).join(', ')}
                </p>
            )}
        </div>
    );
}


/**
 * VideoIntroLinkRow — single row under the worker's optional documents
 * checklist that replaces the previous direct video file upload with a
 * link input (YouTube / Drive / Vimeo / Dropbox).
 *
 * Three render modes:
 *   - Empty + collapsed: shows "Video Introduction Link" + "Add Link" badge
 *   - Editing: shows the input + helper text + Save/Cancel buttons
 *   - Saved: shows the truncated link with edit + remove options
 *
 * If the worker has a LEGACY uploaded video file (hasVideoFile) and no link
 * yet, we surface a small notice that they can replace it with a link.
 */
function VideoIntroLinkRow({ profile, hasVideoFile, input, setInput, editing, setEditing, error, setError, saving, savedMsg, onSave, onRemove }) {
    const savedUrl = profile?.video_intro_url || '';
    const hasSaved = !!savedUrl;

    const beginEdit = () => {
        setInput(savedUrl);
        setError('');
        setEditing(true);
    };
    const cancelEdit = () => {
        setInput('');
        setError('');
        setEditing(false);
    };

    if (editing) {
        return (
            <div className="p-3 border border-zinc-200 space-y-2" data-testid="video-intro-link-edit">
                <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold flex items-center gap-2"><VideoIcon className="w-4 h-4 text-zinc-500" /> Video Introduction Link</span>
                    <Badge variant="outline" className="rounded-none text-[11px] bg-zinc-50 text-zinc-600 border-zinc-300">Upload</Badge>
                </div>
                <Input
                    type="url"
                    inputMode="url"
                    autoComplete="off"
                    placeholder="Paste your YouTube, Google Drive, Vimeo, or Dropbox link here"
                    value={input}
                    onChange={e => { setInput(e.target.value); if (error) setError(''); }}
                    maxLength={500}
                    className={`rounded-none h-10 text-sm ${error ? 'border-red-400' : ''}`}
                    data-testid="video-intro-link-input"
                />
                {error && <p className="text-xs text-red-600" data-testid="video-intro-link-error">{error}</p>}
                <p className="text-[11px] text-zinc-500 leading-relaxed">
                    Record a 30–60 second introduction video, upload it to YouTube (you can set it to <strong>Unlisted</strong> so only people with the link can view it) or Google Drive, then paste the link here.
                </p>
                <p className="text-[11px] text-zinc-400 leading-relaxed">This is optional but strongly recommended — it helps employers get to know you better.</p>
                <div className="flex gap-2 pt-1">
                    <Button
                        size="sm"
                        className="rounded-none bg-zinc-900 text-white text-xs h-8"
                        onClick={onSave}
                        disabled={saving}
                        data-testid="video-intro-link-save"
                    >
                        {saving ? 'Saving…' : (hasSaved ? 'Save Changes' : 'Save Link')}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        className="rounded-none text-xs h-8"
                        onClick={cancelEdit}
                        disabled={saving}
                        data-testid="video-intro-link-cancel"
                    >
                        Cancel
                    </Button>
                </div>
            </div>
        );
    }

    if (hasSaved) {
        return (
            <div className="p-2 border border-green-100 bg-green-50 flex items-center justify-between gap-2 flex-wrap" data-testid="video-intro-link-saved">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <a
                        href={savedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-green-800 hover:underline truncate"
                        title={savedUrl}
                        data-testid="video-intro-link-href"
                    >
                        {truncateUrl(savedUrl, 40)}
                    </a>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                        type="button"
                        onClick={beginEdit}
                        className="text-xs text-zinc-600 hover:text-zinc-900 underline"
                        data-testid="video-intro-link-edit-btn"
                    >
                        Edit
                    </button>
                    <button
                        type="button"
                        onClick={onRemove}
                        disabled={saving}
                        className="text-xs text-red-600 hover:text-red-800 underline disabled:opacity-50"
                        data-testid="video-intro-link-remove"
                    >
                        Remove link
                    </button>
                </div>
                {savedMsg && (
                    <div className="basis-full text-xs text-green-700" data-testid="video-intro-link-saved-msg">{savedMsg}</div>
                )}
            </div>
        );
    }

    // Empty state — collapsed row with "Add Link" action
    return (
        <div>
            <button
                type="button"
                className="w-full flex items-center justify-between p-2 border border-zinc-100 hover:bg-zinc-50 text-left"
                onClick={beginEdit}
                data-testid="video-intro-link-empty"
            >
                <div className="flex items-center gap-2">
                    <VideoIcon className="w-4 h-4 text-zinc-400" />
                    <span className="text-sm">Video Introduction (30-60 sec)</span>
                </div>
                <Badge variant="outline" className="rounded-none text-[11px] bg-zinc-50 text-zinc-600 border-zinc-300" data-testid="video-intro-link-add-badge">
                    Add Link
                </Badge>
            </button>
            {hasVideoFile && (
                <p className="text-[11px] text-zinc-500 mt-1 px-1" data-testid="video-intro-legacy-notice">
                    You have an uploaded video. You can replace it with a link to a YouTube or Google Drive video above.
                </p>
            )}
            {savedMsg && (
                <p className="text-[11px] text-green-700 mt-1 px-1" data-testid="video-intro-link-saved-msg">{savedMsg}</p>
            )}
        </div>
    );
}
